import React from 'react';

// Edits config.group_role_map — an ordered list of { group, role } pairs.
// `group` is the identity-provider group identifier (object id or name) the
// admin types; `role` is chosen from the client's own roles, so an entry can
// never point at a role that doesn't exist (XPEN-2628 acceptance criterion).
//
// Incomplete rows (blank group and/or role) are intentionally tolerated here
// rather than filtered on commit — filtering would drop a just-added or
// half-typed row mid-edit. The resolver (xpens-api) ignores entries with an
// empty group or role, so an incomplete row is harmless dead config, not a bug.
const GroupRoleMappingSection = ({ updatedClient, data, updateField }) => {
    const t = (key) => data.t.read(key);
    const roles = updatedClient.config?.roles || [];
    const entries = updatedClient.config?.group_role_map || [];

    const commit = (next) => updateField('config', next, 'group_role_map');

    const updateEntry = (index, patch) =>
        commit(entries.map((e, i) => (i === index ? { ...e, ...patch } : e)));

    const removeEntry = (index) => commit(entries.filter((_, i) => i !== index));

    const addEntry = () => commit([...entries, { group: '', role: roles[0] || '' }]);

    return (
        <div className="mb-2">
            <h5 className="mt-4">{t('group_role_mapping.title')}</h5>
            <p className="text-muted mb-1">{t('group_role_mapping.description')}</p>
            {entries.length > 0 && (
                <small className="text-muted d-block mb-2">{t('group_role_mapping.order_hint')}</small>
            )}

            {entries.length === 0 ? (
                <p className="text-muted font-italic">{t('group_role_mapping.empty')}</p>
            ) : (
                entries.map((entry, index) => (
                    <div className="row align-items-center mb-2" key={index}>
                        <div className="col-6">
                            <input
                                className="form-control"
                                value={entry.group ?? ''}
                                placeholder={t('group_role_mapping.group_placeholder')}
                                onChange={(e) => updateEntry(index, { group: e.target.value })}
                            />
                        </div>
                        <div className="col-4">
                            <select
                                className="form-control"
                                value={entry.role ?? ''}
                                onChange={(e) => updateEntry(index, { role: e.target.value })}
                            >
                                <option value="">{t('group_role_mapping.select_role')}</option>
                                {roles.map((role) => (
                                    // value stays the raw role string (what the resolver matches on);
                                    // the label is localized like role names elsewhere in the app.
                                    <option key={role} value={role}>
                                        {t('role_' + role.toLowerCase()) || role}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeEntry(index)}
                            >
                                {t('group_role_mapping.remove')}
                            </button>
                        </div>
                    </div>
                ))
            )}

            <button type="button" className="btn btn-sm btn-secondary mt-1" onClick={addEntry}>
                {t('group_role_mapping.add')}
            </button>
        </div>
    );
};

export default GroupRoleMappingSection;
